/**
 * Felles biter for Godkjenn-køen (#155, redesign steg 5a): flaten til det
 * store kortet, ventetid-chipen og raden i den kompakte listen.
 */
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { Icon } from '../../components/primitives/Icon';
import { waitingHours } from './useApprovalQueue';

/** Det store kortet: glass, radius 28, 16 px padding (skissens `article`). */
export const FEATURED_CARD = `${GLASS_SURFACE} flex flex-col gap-2.5 rounded-xl p-4 shadow-md`;

/** Den tonede flisen for et belønningsbytte; et bytte har ingen personfarge her. */
export function RewardTile({ size = 36 }: { size?: 36 | 44 }): ReactNode {
  return (
    <span
      aria-hidden
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-full bg-lilac-bg text-lilac',
        size === 44 ? 'h-11 w-11' : 'h-9 w-9',
      ].join(' ')}
    >
      <Icon.NavRewards size={size === 44 ? 22 : 18} />
    </span>
  );
}

interface WaitChipProps extends ComponentPropsWithoutRef<'span'> {
  at: string | null;
  now: Date;
  'data-testid'?: string;
  'data-tier'?: string;
}

/**
 * «26 t» — hvor lenge noe har ventet, i amber (venter = oppmerksomhet). Den
 * synlige teksten er kort; skjermlesere får «venter i 26 timer».
 */
export function WaitChip({ at, now, className, ...rest }: WaitChipProps): ReactNode {
  const { t } = useTranslation();
  const hours = waitingHours(at, now);
  if (hours === null) return null;
  return (
    <span
      {...rest}
      className={[
        'inline-flex h-6.5 shrink-0 items-center rounded-pill bg-warn-bg px-2.5 text-caption font-extrabold text-warn tnum',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span aria-hidden>{t('approvals.queue.waitHours', { count: hours })}</span>
      <span className="sr-only">{t('approvals.queue.waitHoursSr', { count: hours })}</span>
    </span>
  );
}

interface QueueRowProps {
  /** Avatar eller flis, 36 px. */
  lead: ReactNode;
  title: ReactNode;
  meta: ReactNode;
  trailing?: ReactNode;
  onSelect: () => void;
  'data-testid': string;
  'data-approval-item': string;
}

/**
 * Én rad i «Også i køen». Hele raden er en knapp som løfter elementet opp i
 * det store kortet, der handlingene er. Minst 64 px høy (mål ≥ 44 px).
 */
export function QueueRow({
  lead,
  title,
  meta,
  trailing,
  onSelect,
  'data-testid': testId,
  'data-approval-item': itemId,
}: QueueRowProps): ReactNode {
  return (
    <li className="border-b border-card-line last:border-b-0">
      <button
        type="button"
        data-testid={testId}
        data-approval-item={itemId}
        onClick={onSelect}
        className="flex min-h-16 w-full items-center gap-3 py-2 text-left"
      >
        {lead}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-s font-extrabold text-ink">{title}</span>
          <span className="block truncate text-caption text-ink-2">{meta}</span>
        </span>
        {trailing}
        <span aria-hidden className="shrink-0 text-ink-2">
          <Icon.ChevR size={20} />
        </span>
      </button>
    </li>
  );
}
